import { useState, useRef, useEffect } from "react";
import { useMounted } from "hooks/react";
import clsx from "clsx";

import { isMobile } from "utils/browser";

interface SliderProps {
  min: number;
  max: number;
  onChange: (newValue: any) => void;
  initialValue?: number;
}

export default function Slider(props: SliderProps): JSX.Element {
  const { initialValue, min, max: maxProp } = props;
  const { onChange } = props;

  const [value, setValue] = useState(initialValue || min);
  const [max, setMax] = useState(maxProp);

  const [shouldFloatValue, setShouldFloatValue] = useState(false);

  const initialMarginLeft = useRef(null);
  const initialPosition = useRef(null);
  const sliderRef = useRef(null);
  const selectorRef = useRef(null);
  const touching = useRef(null);

  const newMargin = useRef(null);
  const updateScheduler = useRef(null);

  useMounted(() => {
    const newMargin = value * step();
    initialMarginLeft.current = newMargin;
    initialPosition.current =
      sliderRef.current.getBoundingClientRect().left + newMargin;
    selectorRef.current.style.marginLeft = `${newMargin}px`;
  });

  useEffect(() => {
    window.addEventListener("touchmove", handleMove);
    window.addEventListener("mousemove", handleMove);
    window.addEventListener("touchend", handleTouchEnd);
    window.addEventListener("mouseup", handleTouchEnd);

    if (value > max) {
      setValue(max);
      const newMargin = (max - min) * step();
      selectorRef.current.style.marginLeft = `${newMargin}px`;
      initialMarginLeft.current = newMargin;
    } else {
      const newMargin = (value - min) * step();
      selectorRef.current.style.marginLeft = `${newMargin}px`;
      initialMarginLeft.current = newMargin;
    }

    initialPosition.current =
      sliderRef.current.getBoundingClientRect().left + newMargin;

    return () => {
      window.removeEventListener("touchmove", handleMove);
      window.removeEventListener("mousemove", handleMove);
      window.removeEventListener("touchend", handleTouchEnd);
      window.removeEventListener("mouseup", handleTouchEnd);
    };
  }, [max]);

  useEffect(() => {
    setMax(maxProp);
  }, [maxProp]);

  useEffect(() => {
    onChange(value);
  }, [value]);

  function handleTouchStart(event) {
    setShouldFloatValue(true);
    touching.current = true;
    initialPosition.current = positionFromEvent(event);
    initialMarginLeft.current = currentMargin();
  }

  function handleMove(event) {
    if (!touching.current) return;

    newMargin.current = newMarginLeft(event);
    if (newMargin.current || newMargin.current === 0) debounceUpdate();
  }

  function handleTouchEnd(_event) {
    setShouldFloatValue(false);
    touching.current = false;
  }

  function handleClick(event) {
    initialMarginLeft.current = currentMargin();
    const delta =
      event.clientX -
      (currentMargin() + sliderRef.current.getBoundingClientRect().left);
    const newMargin = newMarginLeft(event, delta);
    if (newMargin || newMargin === 0) {
      selectorRef.current.style.marginLeft = `${newMargin}px`;
      initialPosition.current = positionFromEvent(event);
      initialMarginLeft.current = newMargin;
      updateValue(newMargin);
    }
  }

  function positionFromEvent(e) {
    if (
      e.type == "touchstart" ||
      e.type == "touchmove" ||
      e.type == "touchend" ||
      e.type == "touchcancel"
    ) {
      const touch = e.touches[0] || e.changedTouches[0];
      return touch.pageX;
    } else if (
      e.type == "click" ||
      e.type == "mousedown" ||
      e.type == "mouseup" ||
      e.type == "mousemove" ||
      e.type == "mouseover" ||
      e.type == "mouseout" ||
      e.type == "mouseenter" ||
      e.type == "mouseleave"
    ) {
      return e.clientX;
    }
  }

  function newMarginLeft(event, delta = null) {
    delta = delta || positionFromEvent(event) - initialPosition.current;
    if (Math.abs(delta) < step() / 2) return;

    const steps = Math.round(delta / step());
    let newMarginLeft = initialMarginLeft.current + steps * step();
    newMarginLeft = Math.min(newMarginLeft, maxWidth());
    return roundTwo(Math.max(0, newMarginLeft));
  }

  function step() {
    return roundTwo(maxWidth() / (max - min));
  }

  function maxWidth() {
    const slideButtonSize = 14;
    return sliderRef.current.offsetWidth - slideButtonSize / 2;
  }

  function debounceUpdate() {
    if (updateScheduler.current) return;

    if (newMargin.current || newMargin.current === 0)
      selectorRef.current.style.marginLeft = `${newMargin.current}px`;
    updateScheduler.current = setTimeout(() => {
      updateScheduler.current = null;
      if (!newMargin.current && newMargin.current !== 0) return;

      updateValue(newMargin.current);
    }, 50);
  }

  function updateValue(newMargin) {
    setValue(min + Math.round(newMargin / step()));
  }

  function roundTwo(value) {
    return Math.round(value * 100) / 100;
  }

  function currentMargin() {
    const marginMatch = /\d+/.exec(selectorRef.current.style.marginLeft);
    return (marginMatch && Number(marginMatch[0])) || 0;
  }

  return (
    <div
      className="slider relative pb-4 pt-2 w-full"
      style={{ touchAction: "none" }}
    >
      <div className="flex items-center">
        <span className="flex flex-col items-center w-7 text-emphasis-low">
          {min}
        </span>

        <div className="relative flex-grow">
          <div
            className="slider-bar-wrapper py-2 cursor-pointer"
            onClick={handleClick}
          >
            <div
              ref={sliderRef}
              className="slider-bar mx-2 h-1 bg-gray-500 rounded-lg"
            ></div>
          </div>

          <div
            className="md:top-[2px] absolute top-0 flex flex-col items-center"
            ref={selectorRef}
          >
            <div
              className="selector h-[20px] w-[20px] md:h-[14px] md:w-[14px] bg-primary-500 rounded-full cursor-pointer"
              onTouchStart={handleTouchStart}
              onMouseDown={handleTouchStart}
              onDragStart={() => false}
            ></div>
            <span
              className={clsx("selector-value z-10 font-semibold select-none", {
                "absolute bottom-8 p-1 text-2xl rounded-full shadow-xl bg-white border border-gray-200":
                  shouldFloatValue && isMobile()
              })}
            >
              {value}
            </span>
          </div>
        </div>

        <span className="flex flex-col items-center w-7 text-emphasis-low">
          {max}
        </span>
      </div>
    </div>
  );
}
